import {
  getDiscountedPriceValue,
  useCurrentCloudProvider,
} from '@labrav/common'
import {
  Table,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import React from 'react'
import {
  Dimension,
  EulaType,
  Installments,
  MetaData,
  OfferRecipients,
  EntitlementOption,
  PriceTypes,
  ChannelType,
  AzureOfferPlan,
  GCPOfferPlan,
  Features,
  GCPOfferPlanDimesnion,
  PlanTypeGCP,
  GcpPostPaymentTypes,
  dealType,
  AzurePricingType,
} from '../../../modules/privateOffer/types'
import { useStyles } from './DetailedOffer.styles'
import clsx from 'clsx'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import { dateAndTimeConversion } from '../../../common/utils/dateAndTimeConversion'
import {
  isEmpty,
  isNumber,
  mapValues,
  groupBy,
  omit,
  get,
  toUpper,
  isNil,
} from 'lodash'
import { DiscountType, Stakeholders } from '../../../types/flyout'
import { getFilteredDimensions } from '../../../modules/privateOffer/privateOfferCreation/utils/dimensionHelper'
import { getFilteredInstallments } from '../../../modules/privateOffer/privateOfferCreation/utils/installmentHelper'
import { ProductDimensions } from './ProductDimensions'
import { PlanType } from '@labrav/react-components'
import { useTimeZone } from '../../../common/utils/hooks/useTimeZone'
import { CustomerTermSection } from './CustomerTermSection'
import { PlansSection } from './PlansSection'
import { ContainerPricingModel } from '../../../modules/productsListing/types'
import { getDisplayContractDurationValue } from '../../../utils/containerOfferHelperFunctions'
import {
  AdditionalCPPODataKeys,
  getDimensionPrice,
  renewalChannelMapping,
  flatFeeText,
  getTcvEstimateDetail,
} from './utils'

export {
  AdditionalCPPODataKeys,
  getDimensionPrice,
  renewalChannelMapping,
} from './utils'

export type AdditionalCPPOData = {
  label: string
  key: string
  value: string | number | string[]
}
export type DetailedOffer = {
  errorMessage: string
  createdAt: string | Date
  offerStatus: string
  dimensions?: Dimension[]
  installments?: Installments[]
  metaData: MetaData[]
  agreementStartDate?: string
  offerExpirationDate?: string
  companyName: string
  awsAccountId?: string
  offerRecipients: OfferRecipients[]
  emailCustomMessage: string
  offerName: string
  serviceLength?: number
  subscriptionEndDate?: string | null
  zeroDollarPricing?: boolean
  eulaType: EulaType
  eulaFile?: string[]
  renewal?: boolean
  renewalCount?: number
  renewalChannel?: ChannelType | null
  dealType?: dealType
  offerDescription?: string
  additionalCPPOData?: AdditionalCPPOData[]
  stakeholders?: Stakeholders[]
  resellerName?: string
  flexiblePaymentTerms?: boolean
  entitlementOption?: EntitlementOption
  isCustomPricing?: boolean
  billingAccountId?: string
  variableStartDate?: boolean
  offerStartDate?: string
  acceptBy?: string
  plans?: AzureOfferPlan[]
  azurePricingType?: AzurePricingType
  preparedBy?: string
  buyerCloudBillingId?: string
  plan?: GCPOfferPlan
  contractDuration?: number
  labraPlanFeatures?: Features[]
  autoRenew?: boolean
  notes?: string
  containerOfferServiceLength?: string
  usageDuration?: number
}
export type DetailedOfferProps = {
  offer: DetailedOffer
  pricesArray?: [keyof PriceTypes]
  planType?: PlanType
  isViewPage?: boolean
  contractPricingModel?: ContainerPricingModel
}

export const DetailedOffer: React.FC<DetailedOfferProps> = ({
  offer,
  pricesArray = ['price'],
  planType,
  isViewPage = false,
  contractPricingModel,
}: DetailedOfferProps) => {
  const { classes } = useStyles()
  const isTypeCPPO = !isEmpty(offer.additionalCPPOData)
  const cloudMarketPlace = useCurrentCloudProvider()
  const dimensions = offer.dimensions
    ? getFilteredDimensions(offer.dimensions)
    : []
  const installments = offer.installments
    ? getFilteredInstallments(offer.installments)
    : []
  const hasInstallments = !isEmpty(installments)
  const hasMetaData = !isEmpty(offer.metaData)
  const { timeZone } = useTimeZone()
  const { modifiedDate: offerExpirationDate } = dateAndTimeConversion(
    offer.offerExpirationDate ?? '',
    timeZone
  )
  const { modifiedDate: agreementStartDate } = dateAndTimeConversion(
    offer.agreementStartDate ?? '',
    timeZone
  )
  const { modifiedDate: subscriptionEndDate } = dateAndTimeConversion(
    offer.subscriptionEndDate ?? '',
    timeZone
  )

  const filteredAdditionalCPPOData = mapValues(
    groupBy(offer.additionalCPPOData ?? [], 'label'),
    list => list.map(data => omit(data, 'label'))
  )

  const entitlementOptionMessage =
    offer?.entitlementOption === EntitlementOption.SINGLE
      ? 'Buyer can choose only one of the existing dimensions'
      : 'Buyer can choose one or more existing dimensions'

  const updatedDimensions = dimensions.map(dimension => ({
    ...dimension,
    prices: { ...getDimensionPrice(isTypeCPPO, dimension) },
  }))
  const discountType = get(offer, 'additionalCPPOData', []).find(
    data => data.key === AdditionalCPPODataKeys.DISCOUNT_TYPE
  )?.value
  const licenseFiles = get(offer, 'additionalCPPOData', []).find(
    data => data.key === AdditionalCPPODataKeys.RESELLER_LICENSE_AGREEMENT_FILES
  )?.value as string[]

  const isProductProfessionalServices =
    planType === PlanType?.PROFESSIONAL_SERVICES

  const getEulaHeading = (
    isTypeCPPO: boolean,
    isProductProfessionalServices: boolean
  ) => {
    if (isTypeCPPO) {
      return 'End customer EULA type'
    } else if (isProductProfessionalServices) {
      return 'EULA'
    } else {
      return 'EULA type'
    }
  }
  const { modifiedDate: offerCreatedAt, modifiedTime: offerCreationTime } = dateAndTimeConversion(
    offer.createdAt,
    timeZone
  )
  const status = offer.offerStatus === 'OFFER_CREATION_FAILED' ? 'Failed' : offer.offerStatus
  
  const eulaHeading = getEulaHeading(isTypeCPPO, isProductProfessionalServices)
  return (
    <div data-testid="detailed-offers" className={classes.detailedOffer}>
      {cloudMarketPlace === 'GCP' && (
        <div
          className={classes.offerStatus}
        >
          <div
            className={classes.status}
          >
            <div>
              <div className={classes.statusHeading}>
                Offer status:
              </div>
              <div className={classes.statusText}>
                {status}
              </div>
            </div>
          </div>
          {status === 'Failed' && (
            <>
              <div className={classes.divider} />
              <div className={classes.errorMessage}>
                <div>Issues found: {offer.errorMessage}</div>
                <div className={classes.dateAndTime}>
                  {offerCreatedAt} || {offerCreationTime}
                </div>
              </div>
            </>
          )}
        </div>
      )}
      <div className={classes.box}>
        <div className={classes.row}>
          <div className={classes.cell}>
            <Typography
              className={classes.heading}
              data-testid="company-name-label"
            >
              {isTypeCPPO ? 'Reseller Company name' : 'Company name'}
            </Typography>
            <Typography className={classes.dataText} data-testid="company-name">
              {offer.companyName || '-'}
            </Typography>
          </div>
          {cloudMarketPlace === 'AWS' && (
            <div className={classes.cell}>
              <Typography
                className={classes.heading}
                data-testid="account-id-label"
              >
                {isTypeCPPO ? 'Reseller AWS account ID' : 'AWS Account ID'}
              </Typography>
              <Typography className={classes.dataText} data-testid="account-id">
                {offer.awsAccountId || '-'}
              </Typography>
            </div>
          )}
          {cloudMarketPlace === 'AZURE' && (
            <div className={classes.cell}>
              <Typography
                className={classes.heading}
                data-testid="billing-account-id-label"
              >
                Azure billing account ID
              </Typography>
              <Typography className={classes.dataText} data-testid="account-id">
                {offer.billingAccountId || '-'}
              </Typography>
            </div>
          )}
          {cloudMarketPlace === 'GCP' && (
            <div className={classes.cell}>
              <Typography
                className={classes.heading}
                data-testid="billing-account-id-label"
              >
                GCP billing account ID
              </Typography>
              <Typography className={classes.dataText} data-testid="account-id">
                {offer.buyerCloudBillingId || '-'}
              </Typography>
            </div>
          )}
        </div>
        {cloudMarketPlace === 'AZURE' && (
          <div className={clsx(classes.cell)}>
            <Typography
              className={classes.heading}
              data-testid="prepareby-label"
            >
              Prepared by
            </Typography>
            <Typography className={classes.dataText} data-testid="company-name">
              {offer.preparedBy || '-'}
            </Typography>
          </div>
        )}
        {!isEmpty(offer.resellerName) && (
          <div className={clsx(classes.cell)}>
            <Typography
              className={classes.heading}
              data-testid="reseller-name-label"
            >
              Reseller name
            </Typography>
            <Typography className={classes.dataText} data-testid="company-name">
              {offer.resellerName}
            </Typography>
          </div>
        )}
        <div
          className={clsx(classes.recipientBox, classes.generalPaddingAndWidth)}
        >
          <RecipientsStakeholders
            label={'Recipients'}
            offerRecipients={offer.offerRecipients}
          />
          <Typography
            className={clsx(classes.dataText, classes.customMessage)}
            data-testid="email-mesage"
          >{`Custom email message - ${offer.emailCustomMessage}`}</Typography>
          <div className={classes.offerBox}>
            <div className={classes.offerContainer}>
              <Typography
                className={classes.heading}
                style={{ paddingBottom: '12px' }}
              >
                {isTypeCPPO ? 'Agreement name' : 'Offer name'}
              </Typography>
              <Typography className={classes.dataText} data-testid="offer-name">
                {offer.offerName || '-'}
              </Typography>
            </div>
            {!isEmpty(offer.offerDescription) && (
              <div className={classes.offerContainer}>
                <Typography
                  className={classes.heading}
                  style={{ paddingBottom: '12px' }}
                >
                  {isTypeCPPO ? 'Agreement description' : 'Offer description'}
                </Typography>
                <Typography
                  className={classes.dataText}
                  data-testid="offer-name"
                >
                  {offer.offerDescription || '-'}
                </Typography>
              </div>
            )}
          </div>
          {cloudMarketPlace === 'GCP' && offer?.notes && (
            <Typography
              className={clsx(classes.dataText, classes.customMessage)}
              data-testid="notes-mesage"
            >{`Notes - ${offer.notes}`}</Typography>
          )}
          {(!isEmpty(offer.serviceLength) || isNumber(offer.serviceLength)) && (
            <ServiceLengthAndExpiration
              label={'Service length (in months):'}
              value={`${offer.serviceLength}`}
            />
          )}
          {!isEmpty(offer.subscriptionEndDate) && (
            <ServiceLengthAndExpiration
              label={'Subscription expiry date:'}
              value={subscriptionEndDate}
            />
          )}
        </div>
      </div>
      {cloudMarketPlace === 'AWS' &&
        (!isTypeCPPO ||
          (isTypeCPPO &&
            discountType &&
            discountType !== DiscountType.DISCOUNT)) && (
          <div className={classes.box}>
            <div className={classes.generalPaddingAndWidth}>
              {(!isProductProfessionalServices &&
                !isTypeCPPO &&
                planType !== PlanType.CONTAINER) ||
                (planType === PlanType.CONTAINER &&
                  contractPricingModel === ContainerPricingModel.CONTRACT &&
                  offer?.flexiblePaymentTerms) ? (
                <>
                  <Typography
                    className={clsx(classes.heading, classes.alignDimension)}
                  >
                    {entitlementOptionMessage}
                  </Typography>
                </>
              ) : (
                <Typography
                  className={clsx(classes.heading, classes.alignDimension)}
                >
                  Dimensions
                </Typography>
              )}

              <ProductDimensions
                dimensions={updatedDimensions}
                hasFlexiblePayment={offer.flexiblePaymentTerms}
                pricesArray={pricesArray}
                isTypeCPPO={isTypeCPPO}
              />
            </div>
          </div>
        )}
      {!isEmpty(filteredAdditionalCPPOData) &&
        Object.keys(filteredAdditionalCPPOData ?? []).map(key => {
          return (
            <div className={classes.box}>
              <div
                className={clsx(
                  classes.recipientBox,
                  classes.generalPaddingAndWidth
                )}
              >
                <Typography className={classes.heading}>{key}</Typography>
                <div
                  className={clsx(classes.pricingContainer, classes.fixedTop)}
                >
                  {filteredAdditionalCPPOData[key].map(
                    cppoData =>
                      cppoData.key !==
                      AdditionalCPPODataKeys.RESELLER_LICENSE_AGREEMENT_FILES && (
                        <div className={classes.installmentsTableCell}>
                          <Typography
                            className={clsx(
                              classes.tableCell,
                              classes.heading,
                              classes.additionalPricing
                            )}
                            data-testid={`${cppoData.key}-key`}
                          >
                            {cppoData.key}
                          </Typography>
                          {cppoData.key ===
                            AdditionalCPPODataKeys.RESELLER_LICENSE_AGREEMENT_TYPE ? (
                            <>
                              <Typography
                                className={clsx(
                                  classes.dataText,
                                  classes.headingPadding
                                )}
                                data-testid="license"
                              >
                                {toUpper((cppoData?.value || '-') as string)}
                              </Typography>
                              <div
                                className={clsx(
                                  classes.tableCell,
                                  classes.dataText,
                                  classes.headingPadding
                                )}
                              >
                                {toUpper((cppoData?.value || '') as string) ===
                                  EulaType.CUSTOM &&
                                  !isEmpty(licenseFiles) && (
                                    <CustomEulaDiv
                                      eulaFiles={licenseFiles || []}
                                      label={(cppoData?.value as string) || ''}
                                    />
                                  )}
                              </div>
                            </>
                          ) : (
                            <Typography
                              className={clsx(
                                classes.tableCell,
                                classes.dataText,
                                classes.headingPadding
                              )}
                              data-testid={`${cppoData.key}-value`}
                            >
                              {(cppoData?.value || '')
                                .toString()
                                .toUpperCase()
                                .replaceAll('_', ' ') || '-'}
                            </Typography>
                          )}
                        </div>
                      )
                  )}
                </div>
              </div>
            </div>
          )
        })}

      {cloudMarketPlace === 'AZURE' && offer?.azurePricingType && (
        <PlansSection offer={offer} isViewPage={isViewPage} />
      )}

      {cloudMarketPlace === 'AWS' &&
        ((isTypeCPPO && hasInstallments) || !isTypeCPPO) && (
          <div className={classes.box}>
            <div
              className={clsx(
                classes.recipientBox,
                classes.generalPaddingAndWidth
              )}
            >
              <Typography className={classes.heading}>Pricing</Typography>
              <div className={clsx(classes.pricingContainer, classes.fixedTop)}>
                {!isTypeCPPO && (
                  <div className={classes.installmentsTableCell}>
                    <Typography
                      className={clsx(
                        classes.tableCell,
                        classes.heading,
                        classes.additionalPricing
                      )}
                      data-testid="additional_pricing"
                    >
                      Additional pricing configuration
                    </Typography>
                    <Typography
                      className={clsx(
                        classes.tableCell,
                        classes.dataText,
                        classes.headingPadding
                      )}
                      data-testid="zero-pricing"
                    >
                      Zero Dollar Pricing{' '}
                      {offer.zeroDollarPricing ? 'Enabled' : 'Disabled'}
                    </Typography>
                  </div>
                )}
                {hasInstallments && (
                  <Table
                    aria-label="simple table"
                    data-testid="installment-table"
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell
                          className={clsx(
                            classes.tableCell,
                            classes.installmentsTableCell
                          )}
                        >
                          <Typography
                            className={clsx(
                              classes.tableCell,
                              classes.heading,
                              classes.regularText
                            )}
                            data-testid="installments"
                          >
                            Installments
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    {installments.map(installment => (
                      <ShowInstallmentsRow
                        installment={installment}
                        timeZone={timeZone}
                      />
                    ))}
                  </Table>
                )}
              </div>
            </div>
          </div>
        )}
      {cloudMarketPlace === 'AWS' && planType === PlanType.CONTAINER && (
        <div className={classes.box}>
          <div className={classes.row}>
            <div className={clsx(classes.cell, classes.eulaContainer)}>
              <Typography
                className={clsx(classes.heading, classes.eula)}
                data-testid="contract-duration"
              >
                Contract duration
              </Typography>
              <Typography className={clsx(classes.dataText)} data-testid="">
                {getDisplayContractDurationValue(
                  offer?.containerOfferServiceLength
                )}
              </Typography>
            </div>
            {!isNil(offer?.usageDuration) ? (
              <div className={clsx(classes.cell, classes.eulaContainer)}>
                <Typography
                  className={clsx(classes.heading, classes.eula)}
                  data-testid="usage-duration"
                >
                  Usage duration
                </Typography>
                <Typography
                  className={clsx(classes.dataText)}
                  data-testid="usage-duration-value"
                >
                  {offer?.usageDuration} days
                </Typography>
              </div>
            ) : (
              <></>
            )}
          </div>
        </div>
      )}
      {cloudMarketPlace === 'AZURE' ? (
        <CustomerTermSection offer={offer} />
      ) : (
        <></>
      )}
      {cloudMarketPlace === 'GCP' && (
        <div className={classes.box}>
          <div className={classes.row}>
            <div
              className={clsx(
                classes.cell,
                classes.largePaddingCell,
                classes.noBorder
              )}
            >
              <Typography
                className={clsx(classes.heading, classes.gcpHeadingFont)}
                data-testid="plan-heading"
              >
                {'Plan'}
              </Typography>
              <Typography
                className={clsx(classes.dataText)}
                data-testid="plan-type-value"
              >
                {offer.plan?.planType || '-'}
              </Typography>
            </div>
            <div
              className={clsx(
                classes.cell,
                classes.largePaddingCell,
                classes.noBorder
              )}
            >
              <Typography
                className={clsx(classes.heading, classes.gcpHeadingFont)}
                data-testid="plan-description"
              >
                {'Description'}
              </Typography>
              <Typography
                className={clsx(classes.dataText)}
                data-testid="plan-description-value"
              >
                {offer.plan?.description || '-'}
              </Typography>
            </div>
          </div>
          <div className={classes.row}>
            <div
              className={clsx(
                classes.cell,
                classes.fullCell,
                classes.largePaddingCell
              )}
            >
              <Typography
                className={clsx(classes.heading, classes.gcpHeadingFont)}
                data-testid="payment-schedule"
              >
                {'Payment Schedule'}
              </Typography>
              <Typography
                className={classes.dataText}
                data-testid="payment-schedule-value"
              >
                {offer.plan?.paymentType || '-'}
              </Typography>
            </div>
            <div
              className={clsx(
                classes.cell,
                classes.fullCell,
                classes.largePaddingCell
              )}
            >
              <Typography
                className={clsx(classes.heading, classes.gcpHeadingFont)}
                data-testid="contract-duration"
              >
                {`Contract duration ${offer.plan?.paymentType}`}
              </Typography>
              <Typography
                className={classes.dataText}
                data-testid="contract-duration-value"
              >
                {offer.plan?.serviceLength || '-'}
              </Typography>
            </div>
          </div>
          <div className={classes.rowCell}>
            <div className={clsx(classes.cell, classes.noBorderCell)}>
              <div className={classes.contracts}>
                {offer.plan?.planType &&
                  offer.plan?.planType !== PlanTypeGCP.USAGE && (
                    <div>
                      <Typography
                        className={classes.planHeading}
                        data-testid="total-contract-value"
                      >
                        {'Total Contract Value'}
                      </Typography>
                      <Typography
                        className={classes.dataText}
                        data-testid="total-contract-value-field"
                      >
                        {offer?.plan?.paymentType &&
                          GcpPostPaymentTypes.includes(
                            offer?.plan?.paymentType
                          ) &&
                          offer.plan?.serviceLength &&
                          offer.plan?.flatFeePrice
                          ? getTcvEstimateDetail(
                            offer.plan?.serviceLength as number,
                            offer.plan?.flatFeePrice as number
                          )
                          : offer.plan?.paymentType === ('CUSTOM' as string)
                            ? offer.plan?.tcv
                            : '-'}
                      </Typography>
                    </div>
                  )}
                {offer.plan?.installmentSchedule && (
                  <div>
                    <Typography
                      className={classes.planHeading}
                      data-testid="installment-type"
                    >
                      {'Installment schedule'}
                    </Typography>
                    <Typography
                      className={classes.dataText}
                      data-testid="installment-type-value"
                    >
                      {offer.plan?.installmentSchedule || '-'}
                    </Typography>
                  </div>
                )}
                {offer.plan?.flatFeePrice && (
                  <div>
                    <Typography
                      className={classes.planHeading}
                      data-testid="contract-value"
                    >
                      {`${flatFeeText(offer?.plan?.paymentType)} Flat Fee`}
                    </Typography>
                    <Typography
                      className={classes.dataText}
                      data-testid="contract-value-field"
                    >
                      {offer.plan?.flatFeePrice || '-'}
                    </Typography>
                  </div>
                )}
                {offer?.renewalCount && (
                  <div>
                    <Typography
                      className={classes.planHeading}
                      data-testid="auto-renew-value"
                    >
                      {'Number of renewals'}
                    </Typography>
                    <Typography
                      className={classes.dataText}
                      data-testid="auto-renewal-value-field"
                    >
                      {offer?.renewalCount || '-'}
                    </Typography>
                  </div>
                )}
                <div className={classes.featuresList}>
                  <div>
                    <Typography
                      className={classes.planHeading}
                      data-testid="features-list"
                    >
                      {'Features'}
                    </Typography>
                  </div>
                  {!isEmpty(offer.labraPlanFeatures) ? (
                    offer.labraPlanFeatures?.map(feature => (
                      <div key={JSON.stringify(feature)}>
                        <Typography
                          className={classes.dataText}
                          data-testid="features-list-label"
                        >
                          {feature.label || '-'}
                        </Typography>
                        <Typography
                          className={clsx(
                            classes.planHeading,
                            classes.featureValue
                          )}
                          data-testid="features-list-value"
                        >
                          {feature.value || '-'}
                        </Typography>
                      </div>
                    ))
                  ) : (
                    <div className={classes.planHeading}>-</div>
                  )}
                  <div>
                    <div>
                      <Typography
                        className={classes.planHeading}
                        data-testid="auto-renew"
                      >
                        {'Configure auto-renew'}
                      </Typography>
                    </div>
                    <Typography
                      className={classes.dataText}
                      data-testid="features-list-label"
                    >
                      {offer.autoRenew ? 'Yes' : 'No'}
                    </Typography>
                  </div>
                </div>
              </div>
              <div className={classes.contracts}>
                {offer.plan?.discount && !offer.plan?.individualDiscount && (
                  <div>
                    <Typography
                      className={classes.planHeading}
                      data-testid="contract-value"
                    >
                      {'Discount on Usage'}
                    </Typography>
                    <Typography
                      className={classes.dataText}
                      data-testid="contract-value-field"
                    >
                      {offer.plan?.discount || '-'}
                    </Typography>
                  </div>
                )}
                <div className={classes.installment}>
                  <div>
                    <Typography
                      className={classes.planHeading}
                      data-testid="billing-account-id-label"
                    >
                      {offer.plan?.metricPricing
                        ? 'Meteric Discounts'
                        : 'Installment Schedule'}
                    </Typography>
                  </div>
                  {!isEmpty(offer?.plan?.metricPricing) ? (
                    <GCPPlanMetricDimension
                      metrics={offer?.plan?.metricPricing || []}
                      discount={offer?.plan?.discount}
                      individualDiscount={offer?.plan?.individualDiscount}
                    />
                  ) : !isEmpty(offer?.plan?.installmentInfo) ? (
                    offer.plan?.installmentInfo?.map((installment, index) => (
                      <div>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                          }}
                        >
                          <div>
                            <Typography
                              className={clsx(
                                classes.planHeading,
                                classes.featureValue,
                                classes.installmentFont
                              )}
                              data-testid="installment-label"
                            >
                              {`Installment date ${index + 1}`}
                            </Typography>
                            <Typography
                              className={classes.dataText}
                              data-testid="installment-date"
                            >
                              {installment.installmentDate || '-'}
                            </Typography>
                          </div>
                          <div>
                            <Typography
                              className={clsx(
                                classes.planHeading,
                                classes.featureValue,
                                classes.installmentFont
                              )}
                              data-testid="installment-flat-fee"
                            >
                              {`Flat fee ${index + 1}`}
                            </Typography>
                            <Typography
                              className={classes.dataText}
                              data-testid="installment-flat-fee-value"
                            >
                              {installment.flatFeePrice || '-'}
                            </Typography>
                          </div>
                        </div>
                        <div style={{ marginTop: '10px' }}>
                          <GCPPlanMetricDimension
                            metrics={installment?.metricPricing || []}
                            discount={offer?.plan?.discount}
                            individualDiscount={offer?.plan?.individualDiscount}
                          />
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className={classes.planHeading}>-</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {cloudMarketPlace === 'AZURE' && (
        <div className={classes.box}>
          <div className={classes.row}>
            {cloudMarketPlace === 'AZURE' && (
              <div className={clsx(classes.cell, classes.eulaContainer)}>
                <Typography
                  className={clsx(classes.heading, classes.eula)}
                  data-testid="eula-heading"
                >
                  Eula
                </Typography>
                <div>
                  {!isEmpty(offer?.eulaFile) ? (
                    <CustomEulaDiv
                      eulaFiles={offer?.eulaFile || []}
                      label={offer.eulaType}
                    />
                  ) : (
                    <>-</>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {cloudMarketPlace !== 'AZURE' && (
        <div className={classes.box}>
          <div className={classes.row}>
            <div className={clsx(classes.cell, classes.eulaContainer)}>
              <Typography
                className={clsx(classes.heading, classes.eula)}
                data-testid="eula-heading"
              >
                {eulaHeading}
              </Typography>
              <Typography
                className={clsx(classes.dataText)}
                data-testid="eula-type"
              >
                {offer.eulaType}
              </Typography>
            </div>
            {!isTypeCPPO &&
              cloudMarketPlace === 'AWS' &&
              planType === PlanType.SAAS_CONTRACT && (
                <div
                  className={clsx(classes.cell, classes.installmentsTableCell)}
                >
                  <Typography
                    className={clsx(classes.heading, classes.eula)}
                    data-testid="start-date-label"
                  >
                    Start date
                  </Typography>
                  <Typography
                    className={clsx(classes.dataText, classes.regularText)}
                    data-testid="agreement-start-date"
                  >
                    {getAWSStartDateValue(offer, agreementStartDate)}
                  </Typography>
                </div>
              )}
            {((!isTypeCPPO && cloudMarketPlace === 'AWS') ||
              cloudMarketPlace === 'GCP') && (
                <div
                  className={clsx(classes.cell, classes.installmentsTableCell)}
                >
                  <Typography
                    className={clsx(classes.heading, classes.eula)}
                    data-testid="offer-expiration-label"
                  >
                    Offer expiration date
                  </Typography>
                  <Typography
                    className={clsx(classes.dataText, classes.regularText)}
                    data-testid="offer-expiration"
                  >
                    {isEmpty(offer.offerExpirationDate)
                      ? 'N.A.'
                      : offerExpirationDate}
                  </Typography>
                </div>
              )}
          </div>
          {!isEmpty(offer?.eulaFile) &&
            (!isTypeCPPO ||
              (isTypeCPPO && offer?.eulaType === EulaType.CUSTOM)) && (
              <div
                className={classes.eulaButtonContainer}
                data-testid="eula-button-container"
              >
                <CustomEulaDiv
                  eulaFiles={offer?.eulaFile || []}
                  label={offer.eulaType}
                />
              </div>
            )}
        </div>
      )}
      {cloudMarketPlace === 'GCP' && (
        <div className={classes.box}>
          <div className={classes.row}>
            <div className={clsx(classes.cell, classes.eulaContainer)}>
              <Typography
                className={clsx(classes.heading, classes.eula)}
                data-testid="renewal-channel"
              >
                Deal Type
              </Typography>
              <Typography
                className={clsx(classes.dataText)}
                data-testid="renewal-type"
              >
                {renewalChannelMapping[offer?.dealType || '-']}
              </Typography>
            </div>
          </div>
        </div>
      )}
      {hasMetaData && (
        <div className={classes.box}>
          <div
            className={clsx(
              classes.recipientBox,
              classes.generalPaddingAndWidth
            )}
          >
            <Typography className={classes.heading} data-testid="meta-data">
              Contract meta data
            </Typography>
            <Table aria-label="simple table" className={classes.metaTable}>
              <TableHead>
                <TableRow>
                  <TableCell className={classes.installmentsTableCell}>
                    <Typography
                      className={clsx(
                        classes.tableCell,
                        classes.heading,
                        classes.regularText
                      )}
                      style={{ textAlign: 'left' }}
                    >
                      Key
                    </Typography>
                  </TableCell>
                  <Typography
                    className={clsx(
                      classes.tableCell,
                      classes.heading,
                      classes.regularText
                    )}
                  >
                    Value
                  </Typography>
                </TableRow>
              </TableHead>
              {offer.metaData.map(metaData => (
                <TableRow>
                  <TableCell className={classes.installmentsTableCell}>
                    <Typography
                      className={clsx(classes.tableCell, classes.recepientText)}
                      data-testid={`meta-data-${metaData.key}`}
                    >
                      {metaData.key}
                    </Typography>
                  </TableCell>
                  <TableCell className={classes.installmentsTableCell}>
                    <Typography
                      className={clsx(classes.tableCell, classes.recepientText)}
                      data-testid={`meta-data-vaue-${metaData.key}`}
                    >
                      {metaData.value}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </Table>
          </div>
        </div>
      )}
      {!isEmpty(offer.stakeholders) && (
        <div className={classes.box}>
          <div
            className={clsx(
              classes.recipientBox,
              classes.generalPaddingAndWidth
            )}
          >
            <RecipientsStakeholders
              label={'Stakeholders'}
              offerRecipients={offer.stakeholders ?? []}
            />
          </div>
        </div>
      )}
    </div>
  )
}

export type RecipientsStakeholders = {
  label: string
  offerRecipients: OfferRecipients[]
}

export const RecipientsStakeholders = React.memo(
  ({ label, offerRecipients }: RecipientsStakeholders) => {
    const { classes } = useStyles()
    return (
      <>
        <Typography className={classes.heading} data-testid={`${label}-label`}>
          {label}
        </Typography>
        {offerRecipients?.map(recipient => (
          <div className={classes.tableRow}>
            <Typography className={clsx(classes.tableCell, classes.dataText)}>
              {getRecipientStakeholdersName(recipient)}
            </Typography>
            <Typography
              className={clsx(classes.tableCell, classes.recepientText)}
            >
              {recipient.title}
            </Typography>
            <Typography
              className={clsx(classes.tableCell, classes.recepientText)}
            >
              {recipient.email}
            </Typography>
          </div>
        ))}
      </>
    )
  }
)

const getRecipientStakeholdersName = (recipient: OfferRecipients) => {
  if (recipient?.name) {
    return recipient?.name
  } else {
    return `${recipient.firstName || ''} ${recipient.lastName || ''}`.trim()
  }
}

export type CustomEulaDivProps = {
  eulaFiles: string[]
  label: string
}
export const CustomEulaDiv = React.memo(
  ({ eulaFiles, label }: CustomEulaDivProps) => {
    const { classes } = useStyles()
    const openEula = (eulaFiles?: string[]) => () => {
      if (!isEmpty(eulaFiles)) {
        eulaFiles?.forEach(file => {
          const newWind = window.open(file, '_blank', 'noopener noreferrer')
          if (newWind) {
            newWind.opener = null
          }
        })
      }
    }
    return (
      <div
        className={classes.eulaButton}
        onClick={openEula(eulaFiles)}
        data-testid="custom-eula-button"
      >
        <PictureAsPdfIcon className={classes.pdfIcon} />
        <Typography className={clsx(classes.dataText, classes.regularText)}>
          {label}
        </Typography>
      </div>
    )
  }
)

export type showInstallmentsRowProps = {
  installment: Installments
  timeZone: string
}
export const ShowInstallmentsRow = React.memo(
  ({ installment, timeZone }: showInstallmentsRowProps) => {
    const { classes } = useStyles()
    const { modifiedDate: paymentDate } = dateAndTimeConversion(
      installment.paymentDate,
      timeZone
    )
    return (
      <TableRow data-testid="installment-row">
        <TableCell className={classes.installmentsTableCell}>
          <div className={classes.installmentData}>
            <Typography
              className={clsx(classes.tableCell, classes.recepientText)}
            >
              {`${installment.currency} `}
              {installment.amount}
            </Typography>
            <Typography
              className={clsx(classes.tableCell, classes.recepientText)}
            >
              {paymentDate}
            </Typography>
          </div>
        </TableCell>
      </TableRow>
    )
  }
)

export type ServiceLengthAndExpirationProps = {
  label: string
  value: string
}

export const ServiceLengthAndExpiration = React.memo(
  ({ label, value }: ServiceLengthAndExpirationProps) => {
    const { classes } = useStyles()
    return (
      <div>
        <div className={clsx(classes.pricingContainer)}>
          <Typography className={classes.heading}>{label}</Typography>
          &nbsp;
          <Typography
            className={classes.dataText}
            data-testid="service-expiry"
          >{` ${value}`}</Typography>
        </div>
      </div>
    )
  }
)

const GCPPlanMetricDimension = ({
  metrics,
  individualDiscount,
  discount,
}: {
  metrics: GCPOfferPlanDimesnion[]
  individualDiscount?: boolean
  discount?: number
}) => {
  const { classes } = useStyles()
  return metrics?.map((meteric, index) => (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
      }}
    >
      <div>
        <Typography
          className={clsx(
            classes.planHeading,
            classes.featureValue,
            classes.installmentFont
          )}
          data-testid="installment-label"
        >
          {`Meteric label ${index + 1}`}
        </Typography>
        <Typography className={classes.dataText} data-testid="installment-date">
          {meteric.label || '-'}
        </Typography>
      </div>
      <div>
        <Typography
          className={clsx(
            classes.planHeading,
            classes.featureValue,
            classes.installmentFont
          )}
          data-testid="installment-flat-fee"
        >
          {`Price ${index + 1}`}
        </Typography>
        <Typography
          className={classes.dataText}
          data-testid="installment-flat-fee-value"
        >
          {'$' +
            `${getDiscountedPriceValue({
              price: meteric.price as number,
              discount: individualDiscount ? meteric.discount : discount,
            })}` || '-'}
        </Typography>
      </div>
      <div>
        <Typography
          className={clsx(
            classes.planHeading,
            classes.featureValue,
            classes.installmentFont
          )}
          data-testid="installment-flat-fee"
        >
          {`Discount ${index + 1}`}
        </Typography>
        <Typography
          className={classes.dataText}
          data-testid="installment-flat-fee-value"
        >
          {individualDiscount ? meteric.discount || '-' : discount || '-'}
        </Typography>
      </div>
    </div>
  ))
}

export const getAWSStartDateValue = (
  offer: DetailedOffer,
  agreementStartDate: string
) => {
  if (offer?.agreementStartDate) {
    return agreementStartDate
  }
  if (offer?.variableStartDate) {
    return 'Accepted date'
  }
  return '-'
}
